import { ApiError, apiFetchStream } from "@/lib/api-client";
import type { AttachFileRecord } from "@/components/admin/attach-picker/types";
import type { RemoteAttachEvent, RemoteAttachProgress } from "./remote-progress";
import type { UploadKind } from "./kinds";

export type RemoteAttachResult = AttachFileRecord & { imported?: boolean; reused?: boolean };
export const REMOTE_ATTACH_MAX_ROWS = 50;

export type RemoteAttachTask = {
  id: string;
  url: string;
  status: "idle" | "queued" | "uploading" | "success" | "error" | "duplicate";
  progress: RemoteAttachProgress | null;
  error: string;
};

export function createRemoteAttachTask(url = "", id = crypto.randomUUID()): RemoteAttachTask {
  return { id, url, status: "idle", progress: null, error: "" };
}

export function remoteAttachUrlKey(raw: string): string {
  const value = raw.trim();
  if (/^https?:\/\//i.test(value)) {
    try { const url = new URL(value); url.hash = ""; return url.href; }
    catch { /* 原校验由上传接口反馈，不能把无效地址改成有效地址。 */ }
  }
  return value.split("#", 1)[0];
}

export type RemotePasteIssue = { line: number; index: number; reason: "invalid" | "ambiguous" };
export type RemotePasteResult =
  | { ok: true; added: number; duplicates: number; overflow: number; firstIndex?: number }
  | { ok: false; reason: "empty" | "busy" | "readonly" }
  | { ok: false; reason: "invalid" | "ambiguous"; issue: RemotePasteIssue };

const remoteAddressStart = /^(?:https?:\/\/|\/(?!\/)|(?:localhost|[a-z0-9-]+(?:\.[a-z0-9-]+)+)(?::\d+)?\/)/i;
const remoteSeparator = /[,，;；]/;

function validRemoteAddress(value: string) {
  if (!remoteAddressStart.test(value) || /[\x00-\x1f\x7f\\]/.test(value)) return false;
  try {
    const url = new URL(value.startsWith("/") ? value : /^https?:\/\//i.test(value) ? value : `https://${value}`, "https://local.invalid");
    return ["http:", "https:"].includes(url.protocol) && Boolean(url.hostname) && !url.username && !url.password;
  } catch { return false; }
}

export function parseRemoteAttachText(text: string): { ok: true; urls: string[] } | { ok: false; issue: RemotePasteIssue } {
  const urls: string[] = [];
  const lines = text.split(/\r\n|\r|\n/);
  for (let lineIndex = 0; lineIndex < lines.length; lineIndex += 1) {
    let index = 0;
    for (const chunk of lines[lineIndex].split("\t")) {
      let value = "", quote = "", quoted = false, closed = false;
      const issue = (reason: RemotePasteIssue["reason"]) => ({ ok: false as const, issue: { line: lineIndex + 1, index: index + 1, reason } });
      const finish = (): boolean => {
        const address = value.trim();
        if (!address) { value = ""; quoted = false; closed = false; return true; }
        if (!validRemoteAddress(address)) return false;
        urls.push(address);
        index += 1;
        value = ""; quoted = false; closed = false;
        return true;
      };
      for (let position = 0; position < chunk.length; position += 1) {
        const char = chunk[position];
        if (quote) {
          if (char === quote) { quote = ""; closed = true; }
          else value += char;
          continue;
        }
        if ((char === '"' || char === "'") && !closed && !value.trim()) {
          quote = char; quoted = true; continue;
        }
        if (remoteSeparator.test(char)) {
          const rest = chunk.slice(position + 1).trimStart();
          const nextAddress = remoteAddressStart.test(rest);
          const query = /[?#]/.test(value);
          if (!quoted && query && nextAddress) return issue("ambiguous");
          const split = closed || !query && (nextAddress || !rest || remoteSeparator.test(rest[0] ?? "") || /^\s/.test(chunk[position + 1] ?? ""));
          if (split) { if (!finish()) return issue("invalid"); continue; }
        }
        if (closed) { if (char.trim()) return issue("invalid"); continue; }

        if (/\s/.test(char) && value.trim() && remoteAddressStart.test(chunk.slice(position).trimStart())) return issue("ambiguous");
        value += char;
      }
      if (quote || !finish()) return issue("invalid");
    }
  }
  return { ok: true, urls };
}

export function mergeRemoteAttachUrls(tasks: RemoteAttachTask[], urls: string[]) {
  const seen = new Set(tasks.filter(task => task.url.trim()).map(task => remoteAttachUrlKey(task.url)));
  const next = [...tasks];
  const empty = tasks.flatMap((task, index) => !task.url.trim() && task.status === "idle" ? [index] : []);
  let added = 0, duplicates = 0, overflow = 0;
  let firstIndex: number | undefined;
  for (const raw of urls) {
    const url = raw.trim();
    if (!url) continue;
    const key = remoteAttachUrlKey(url);
    if (seen.has(key)) { duplicates += 1; continue; }
    seen.add(key);
    const target = empty[added] ?? next.length;
    if (target >= REMOTE_ATTACH_MAX_ROWS) { overflow += 1; continue; }
    firstIndex ??= target;
    next[target] = createRemoteAttachTask(url, next[target]?.id);
    added += 1;
  }
  return { tasks: next, added, duplicates, overflow, firstIndex };
}

export async function uploadRemoteAttachWithProgress(input: {
  url: string; kind: UploadKind; groupId: string; module?: string; visibility?: string;
  signal: AbortSignal; onProgress: (progress: RemoteAttachProgress) => void;
  messages?: { failed: string; network: string };
}): Promise<RemoteAttachResult> {
  const { signal, onProgress, messages, ...body } = input;
  const response = await apiFetchStream("/api/attach/remote", { method: "POST", body, signal, headers: { Accept: "application/x-ndjson" } });
  if (!response.body || !response.headers.get("content-type")?.includes("application/x-ndjson")) throw Error(messages?.network ?? "未收到处理进度，请重试");
  const reader = response.body.getReader(), decoder = new TextDecoder();
  let pending = "", result: RemoteAttachResult | undefined;
  function consume(line: string) {
    if (!line.trim()) return;
    const event = JSON.parse(line) as RemoteAttachEvent<RemoteAttachResult>;
    if (event.type === "progress") onProgress(event.data);
    else if (event.type === "error") throw new ApiError(event.error.message, event.error.status, event.error.code);
    else if (event.type === "complete") result = event.data;
  }
  try {
    for (;;) {
      signal.throwIfAborted();
      const { done, value } = await reader.read();
      pending += decoder.decode(value, { stream: !done });
      const lines = pending.split("\n"); pending = lines.pop()!;
      for (const line of lines) consume(line);
      if (pending.length > 65536) throw Error(messages?.failed ?? "处理响应无效，请重试");
      if (done) break;
    }
    consume(pending);
    if (!result?.id) throw Error(messages?.network ?? "连接已中断，请重试");
    return result;
  } finally { await reader.cancel().catch(() => {}); reader.releaseLock(); }
}
